import React from 'react';
import * as Yup from 'yup';
import { DEFAULT_PROCESSING_FEE_CHECKBOX_LABEL, getCoverProcessingFeesSetting } from '@repo/lib';
import { useFormikContext } from 'formik';
import { Form } from '@dashboard/components/Formik';
import Checkbox from '@dashboard/components/Formik/components/Checkbox';
import usePage from '@dashboard/hooks/usePage';
import { formatPriceInput } from '@dashboard/services/formatting';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import {
  getPageCurrency,
  isFilled,
  PERCENTAGE_OR_AMOUNT,
  ZERO_DECIMAL_CURRENCIES,
} from './feeFormValues';

const emptyToNull = (value, originalValue) => (originalValue === '' ? null : value);

const isAboveZero = value => isFilled(value) && Number(String(value).replace(',', '.')) > 0;

// Turned on with no rate, the box would never show at checkout, so it cannot be saved that way.
const validationSchema = Yup.object()
  .shape({
    percentage: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .min(0, 'Percentage must be 0 or more')
      .max(100, 'Percentage must be 100 or less'),
    amount: Yup.number().transform(emptyToNull).nullable().min(0, 'Amount must be 0 or more'),
  })
  .test(
    'percentage-or-amount',
    'Enter a percentage, a fixed amount or both',
    (values, context) =>
      !values?.enabled ||
      isAboveZero(values?.percentage) ||
      isAboveZero(values?.amount) ||
      context.createError({ path: PERCENTAGE_OR_AMOUNT }),
  );

function getCoverProcessingFeesFormValues(settings, currency) {
  return {
    enabled: Boolean(settings?.enabled),
    percentage: isFilled(settings?.percentage) ? settings.percentage : '',
    amount: isFilled(settings?.amount)
      ? formatPriceForInput({ amount: settings.amount, currency, style: 'decimal' })
      : '',
    label: settings?.label || DEFAULT_PROCESSING_FEE_CHECKBOX_LABEL,
    showAmount: settings?.showAmount ?? true,
    preselected: settings?.preselected ?? false,
    [PERCENTAGE_OR_AMOUNT]: '',
  };
}

function transformCoverProcessingFeesForApi(values, currency) {
  return {
    enabled: values.enabled,
    percentage: isFilled(values.percentage)
      ? parseFloat(String(values.percentage).replace(',', '.'))
      : null,
    amount: isFilled(values.amount) ? formatPriceInput(values.amount, currency) : null,
    label: values.label?.trim() || DEFAULT_PROCESSING_FEE_CHECKBOX_LABEL,
    showAmount: values.showAmount,
    preselected: values.preselected,
  };
}

// Formik keeps a field's touched state after Save, so without this a Save's no-rate error would
// come straight back when the seller turns the section off and on again.
const EnabledCheckbox = () => {
  const { setFieldTouched } = useFormikContext();

  return (
    <Checkbox.Wrapper>
      <Checkbox
        name="enabled"
        id="enabled"
        onChange={() => setFieldTouched(PERCENTAGE_OR_AMOUNT, false, false)}
      />
      <Checkbox.Label htmlFor="enabled">Let customers cover processing fees</Checkbox.Label>
    </Checkbox.Wrapper>
  );
};

const CoverProcessingFeesForm = () => {
  const { page, updatePage } = usePage();
  const currency = getPageCurrency(page);

  return (
    <Form
      validateOnChange
      validationSchema={validationSchema}
      initialValues={getCoverProcessingFeesFormValues(
        getCoverProcessingFeesSetting(page),
        currency,
      )}
      fields={({ values }) => [
        { type: 'custom', name: 'enabled', renderField: () => <EnabledCheckbox /> },
        ...(values.enabled
          ? [
              {
                type: 'columns',
                name: 'fee-rate',
                columns: 2,
                gap: 'sm',
                noMargin: true,
                fields: [
                  {
                    name: 'percentage',
                    label: 'Percentage',
                    type: 'number',
                    placeholder: '0',
                    suffix: '%',
                  },
                  {
                    name: 'amount',
                    label: 'Fixed amount',
                    type: 'number',
                    placeholder: ZERO_DECIMAL_CURRENCIES.includes(currency) ? '0' : '0.00',
                    suffix: (currency || '').toUpperCase(),
                  },
                ],
              },
              { type: 'custom', name: PERCENTAGE_OR_AMOUNT, renderField: () => null },
              {
                type: 'custom',
                name: 'fee-rate-examples',
                renderField: () => (
                  <div className="bg-muted rounded-lg px-2.5 py-2 text-sm text-muted-foreground">
                    Examples: US cards 2.9% + $0.30, EU cards 1.5% + €0.25, UK cards 1.5% + £0.20.
                  </div>
                ),
              },
              { type: 'divider', name: 'checkbox-label-divider' },
              {
                name: 'label',
                label: 'Label',
                type: 'text',
              },
              {
                name: 'showAmount',
                label: 'Show fee amount on label',
                type: 'checkbox',
              },
              {
                name: 'preselected',
                label: 'Preselect fee coverage for customers',
                type: 'checkbox',
              },
            ]
          : []),
      ]}
      onSubmit={async (values, actions) => {
        const coverProcessingFees = transformCoverProcessingFeesForApi(values, currency);
        const { data, error } = await updatePage.mutateAsync({
          coverProcessingFees: [coverProcessingFees],
        });

        if (data) {
          actions.resetForm({
            values: getCoverProcessingFeesFormValues(coverProcessingFees, currency),
          });
        }

        return { data, error };
      }}
    />
  );
};

export default CoverProcessingFeesForm;
